import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { gsap } from 'gsap';
import './Splash.css';

const Splash = () => {
  const mountRef = useRef(null);
  const logoRef = useRef(null);
  const cardsRef = useRef([]);

  const divisions = [
    {
      title: "SIGNATURE SERIES",
      desc: "State-of-the-art luxury and premium elevators tailored for elegance.",
      link: "https://signature-series.lumetiq.in/",
      logoPath: "/assets/img/logo/signature_logo.jpg",
      color: "#885e1f"
    },
    {
      title: "LUMETIQ ELEVATORS",
      desc: "Reliable, efficient, and modern commercial elevator solutions.",
      link: "/home-3",
      logoPath: "/assets/img/logo/lumetiq_logo-removebg-preview.png",
      color: "#009FE1"
    },
    {
      title: "GREEN ENERGY",
      desc: "Innovative solar and renewable energy solutions for a sustainable future.",
      link: "/green",
      logoPath: "/assets/img/bg/green.jpg",
      color: "#10b981"
    },
    {
      title: "FIRE SAFETY",
      desc: "Advanced fire protection and comprehensive safety systems.",
      link: "/fire",
      logoPath: "/assets/img/bg/fire.jpg",
      color: "#ef4444"
    }
  ];

  useEffect(() => {
    // ─── THREE.JS LIGHT THEME ELEVATOR SETUP ────────────────────────
    const mount = mountRef.current;
    let width = window.innerWidth;
    let height = window.innerHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#f0f4f8'); // Premium light blue/grey
    scene.fog = new THREE.Fog('#f0f4f8', 20, 100);

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
    camera.position.set(0, 0, 50);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    // Soft shadows
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);

    // ─── LIGHTING ──────────────────────────────────────────────────
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(20, 50, 20);
    dirLight.castShadow = true;
    dirLight.shadow.camera.top = 50;
    dirLight.shadow.camera.bottom = -50;
    dirLight.shadow.camera.left = -50;
    dirLight.shadow.camera.right = 50;
    scene.add(dirLight);

    const blueLight = new THREE.DirectionalLight(0x009FE1, 0.5);
    blueLight.position.set(-20, 10, 20);
    scene.add(blueLight);

    // ─── 3D ELEVATORS ───────────────────────────────────────────────
    const elevators = [];
    const group = new THREE.Group();
    scene.add(group);

    // Premium Glass Material
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      metalness: 0.1,
      roughness: 0.1,
      transmission: 0.9, // True glass effect
      thickness: 1.0,
      clearcoat: 1.0,
      clearcoatRoughness: 0.1
    });

    // Sleek Metallic Material
    const metalMat = new THREE.MeshStandardMaterial({
      color: 0xe2e8f0,
      metalness: 0.7,
      roughness: 0.2
    });

    // Dark Accent Material
    const darkMetalMat = new THREE.MeshStandardMaterial({
      color: 0x334155,
      metalness: 0.6,
      roughness: 0.4
    });

    for (let i = 0; i < 25; i++) {
      const elGroup = new THREE.Group();

      // The Glass Cabin
      const cabinGeo = new THREE.BoxGeometry(4, 6, 4);
      const cabin = new THREE.Mesh(cabinGeo, glassMat);
      cabin.castShadow = true;
      elGroup.add(cabin);

      // Top Metallic Cap
      const capGeo = new THREE.BoxGeometry(4.2, 0.4, 4.2);
      const topCap = new THREE.Mesh(capGeo, metalMat);
      topCap.position.y = 3.2;
      topCap.castShadow = true;
      elGroup.add(topCap);

      // Bottom Metallic Cap
      const botCap = new THREE.Mesh(capGeo, metalMat);
      botCap.position.y = -3.2;
      botCap.castShadow = true;
      elGroup.add(botCap);

      // Internal light/core
      const coreGeo = new THREE.BoxGeometry(1, 4, 1);
      const coreMat = new THREE.MeshBasicMaterial({ color: 0x009FE1, transparent: true, opacity: 0.2 });
      const core = new THREE.Mesh(coreGeo, coreMat);
      elGroup.add(core);

      // Position logic - Spread them widely across the entire screen
      const x = (Math.random() - 0.5) * 250;
      const z = (Math.random() - 0.5) * 80 - 20;

      elGroup.position.set(x, (Math.random() - 0.5) * 100, z);
      group.add(elGroup);

      // Static Vertical Rails for the elevator
      const railGeo = new THREE.CylinderGeometry(0.15, 0.15, 200, 8);
      const rail1 = new THREE.Mesh(railGeo, darkMetalMat);
      rail1.position.set(x - 2.2, 0, z);
      const rail2 = new THREE.Mesh(railGeo, darkMetalMat);
      rail2.position.set(x + 2.2, 0, z);
      group.add(rail1);
      group.add(rail2);

      elevators.push({
        mesh: elGroup,
        speed: (Math.random() * 0.08 + 0.03) * (Math.random() > 0.5 ? 1 : -1)
      });
    }

    // ─── ANIMATION & PARALLAX ────────────────────────────────────────
    let mouseX = 0;
    let mouseY = 0;
    let targetX = 0;
    let targetY = 0;

    const onMouseMove = (e) => {
      mouseX = (e.clientX - width / 2) * 0.02;
      mouseY = (e.clientY - height / 2) * 0.02;
    };
    window.addEventListener('mousemove', onMouseMove);

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);

      // Gentle camera parallax
      targetX = mouseX;
      targetY = mouseY;
      camera.position.x += (targetX - camera.position.x) * 0.05;
      camera.position.y += (-targetY - camera.position.y) * 0.05;
      camera.lookAt(0, 0, 0);

      // Move elevators
      elevators.forEach(el => {
        el.mesh.position.y += el.speed;
        if (el.mesh.position.y > 60) el.mesh.position.y = -60;
        if (el.mesh.position.y < -60) el.mesh.position.y = 60;
      });

      // Slowly rotate the entire group for dynamic perspective
      group.rotation.y = Math.sin(Date.now() * 0.0001) * 0.2;

      renderer.render(scene, camera);
    };
    animate();

    // ─── RESIZE ──────────────────────────────────────────────────────
    const onResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    // ─── GSAP ELEVATOR ARRIVAL ANIMATION ────────────────────────────
    gsap.fromTo(logoRef.current,
      { y: -50, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.5, ease: "power3.out", delay: 0.2 }
    );

    // 1. Elevators (cards) arrive from the bottom
    gsap.fromTo(cardsRef.current,
      { y: window.innerHeight, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.5, ease: "power3.out", stagger: 0.2, delay: 0.5 }
    );

    // 2. Doors open and interior fades in
    cardsRef.current.forEach((card, i) => {
      const leftDoor = card.querySelector('.elevator-door.left');
      const rightDoor = card.querySelector('.elevator-door.right');
      const interiorContent = card.querySelector('.elevator-interior');

      // Doors slide apart
      gsap.fromTo(leftDoor,
        { xPercent: 0 },
        { xPercent: -100, duration: 1.2, ease: "power2.inOut", delay: 1.8 + i * 0.2 }
      );
      gsap.fromTo(rightDoor,
        { xPercent: 0 },
        { xPercent: 100, duration: 1.2, ease: "power2.inOut", delay: 1.8 + i * 0.2 }
      );

      // Content pops in
      gsap.fromTo(interiorContent,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 0.8, ease: "back.out(1.2)", delay: 2.2 + i * 0.2 }
      );
    });

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
      scene.clear();
      renderer.dispose();
    };
  }, []);

  return (
    <div className="splash-light-wrapper">
      <div ref={mountRef} className="splash-light-canvas"></div>

      <div className="splash-ui-layer">
        <div className="splash-header" ref={logoRef}>
          {/* Note: Using the original colored/dark logo for the light background */}
          <img src={`${process.env.PUBLIC_URL || ''}/assets/img/logo/lumetiq_logo-removebg-preview.png`} alt="Lumetiq Group" className="splash-main-logo" />
          <div className="splash-divider"></div>
          <p className="splash-tagline">LUMETIQ GROUP OF COMPANIES</p>
        </div>

        <div className="splash-cards-container">
          {divisions.map((item, index) => (
            <div
              className="splash-elevator-card"
              key={index}
              ref={el => cardsRef.current[index] = el}
            >
              {/* Elevator Top Frame / Floor Indicator */}
              <div className="elevator-frame-header">
                <div className="floor-indicator" style={{ color: item.color }}>
                  <i className="fas fa-caret-up indicator-arrow"></i>
                  <span>{index + 1}</span>
                </div>
              </div>

              {/* Sliding Metallic Doors */}
              <div className="elevator-doors">
                <div className="elevator-door left">
                  <div className="door-panel"></div>
                </div>
                <div className="elevator-door right">
                  <div className="door-panel"></div>
                </div>
              </div>

              {/* The Content Inside the Elevator */}
              <div className="elevator-interior">
                <div className="card-accent-bar" style={{ backgroundColor: item.color }}></div>
                <div className="card-content">
                  <div className="card-logo-container" style={{ borderColor: `${item.color}50`, boxShadow: `0 0 15px ${item.color}20` }}>
                    <img src={`${process.env.PUBLIC_URL || ''}${item.logoPath}`} alt={item.title} className="card-logo-img" />
                  </div>
                  <h3 className="card-title">{item.title}</h3>
                  <p className="card-desc">{item.desc}</p>
                  {item.link.startsWith('http') ? (
                    <a
                      href={item.link}
                      className="card-link"
                      style={{ color: item.color }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Enter Portal <i className="fas fa-arrow-right"></i>
                    </a>
                  ) : (
                    <Link
                      to={item.link}
                      className="card-link"
                      style={{ color: item.color }}
                    >
                      Enter Portal <i className="fas fa-arrow-right"></i>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Splash;
